import React, { memo, useState } from 'react'
import { Section } from '@shared/components/section'
import YandexLogo from '@shared/svg/YandexLogo.svg'
import FlampLogo from '@shared/svg/FlampLogo.svg'
import styled from 'styled-components'

import { TextReadMore } from './TextReadMore'

const BlockFeedbackComponent = ({ blockTitle, data }: BlockFeedbackProps): JSX.Element => {
	const [isMoreVisible, setIsMoreVisible] = useState(false)
	const hasMore = typeof data?.length === 'number' ? data.length > 3 : false
	const resultList = isMoreVisible ? data : data?.slice(0, 3)
	return (
		<Section>
			<ViewRoot>
				{!!blockTitle && <TextHeader>{blockTitle}</TextHeader>}
				<ViewOthersRow>
					<TextOthers>Больше отзывов</TextOthers>
					<ButtonYandex href='/' target='_blank' rel='noopener noreferrer nofollow'>
						<ImageYandex />
					</ButtonYandex>
					<ButtonFlamp href='/' target='_blank' rel='noopener noreferrer nofollow'>
						<ImageFlamp />
					</ButtonFlamp>
				</ViewOthersRow>
				{!!resultList?.length && (
					<>
						<ViewRowCards>
							{resultList.map((item, idx) => (
								<ColCard key={idx}>
									<ViewCard>
										<GridCardHeader>
											{!!item.image && (
												<CellAva>
													<ImageAva src={item.image} alt={item.name || 'Пользователь'} />
												</CellAva>
											)}
											<CellTitle>{item.name || <br />}</CellTitle>
											<CellSubtitle>{item.position || <br />}</CellSubtitle>
										</GridCardHeader>
										<TextCardTitle>{item.reason || <br />}</TextCardTitle>
										{!!item.text && (
											<TextCard>
												<TextReadMore>{item.text}</TextReadMore>
											</TextCard>
										)}
									</ViewCard>
								</ColCard>
							))}
						</ViewRowCards>
						{hasMore && !isMoreVisible && (
							<ViewMore>
								<ButtonMore type='button' onClick={() => setIsMoreVisible(true)}>
									Показать больше
								</ButtonMore>
							</ViewMore>
						)}
					</>
				)}
			</ViewRoot>
		</Section>
	)
}

const ViewMore = styled.div(() => ({}))

const ButtonMore = styled.button(({ theme }) => ({
	display: 'flex',
	width: '100%',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '16px',
	border: '1px solid black',
	borderRadius: 8,
	backgroundColor: theme.colors.mainwhite,
	'&:hover': {
		transform: 'scaleY(1.05)',
	},
	'&:active': {
		opacity: 0.75,
	},
	...theme.typography.header24_32,
	marginTop: 40,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		width: 'auto',
		padding: '24px 70px',
		marginLeft: 'auto',
		marginRight: 'auto',
		marginTop: 60,
	},
	[theme.mediaQueries.mediaMinWidthTablet]: {
		marginTop: 40,
	},
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		...theme.typography.header20_24,
		padding: '20px 113px',
		marginTop: 32,
	},
}))

const TextCard = styled.p(({ theme }) => ({
	whiteSpace: 'pre-wrap',
	...theme.typography.text16_22,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
	},
	[theme.mediaQueries.mediaMinWidthTablet]: {
		...theme.typography.text14_20,
	},
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		...theme.typography.text18_24,
	},
}))

const TextCardTitle = styled.h3(({ theme }) => ({
	...theme.typography.text20_28,
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
	},
	[theme.mediaQueries.mediaMinWidthTablet]: {
		...theme.typography.text20_28,
	},
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		...theme.typography.header24_32,
	},
	[theme.mediaQueries.mediaTabletToDesktop]: {
		marginBottom: 16,
	},
}))

const CellSubtitle = styled.p(({ theme }) => ({
	gridArea: 'subtitle',
	paddingTop: 16,
	...theme.typography.text12_16,
	opacity: 0.5,
	[theme.mediaQueries.mediaTabletToDesktop]: {
		paddingTop: 0,
	},
}))

const CellTitle = styled.p(({ theme }) => ({
	gridArea: 'title',
	...theme.typography.text18_24,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text20_28,
	},
}))

const ImageAva = styled.img(({ theme }) => ({
	width: 64,
	height: 64,
	objectFit: 'cover',
	borderRadius: '50%',
	overflow: 'hidden',
}))

const CellAva = styled.div(({ theme }) => ({
	gridArea: 'ava',
	paddingRight: 13,
}))

const GridCardHeader = styled.div(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: '77px auto',
	gridTemplateRows: 'auto 1fr auto',
	gridTemplateAreas: '"ava title" "ava icon" "subtitle subtitle"',
	[theme.mediaQueries.mediaTabletToDesktop]: {
		gridTemplateAreas: '"ava title" "ava subtitle" "icon icon"',
	},
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 12,
	},
	[theme.mediaQueries.mediaMinWidthTablet]: {
		marginBottom: 24,
	},
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		marginBottom: 24,
	},
}))

const ViewCard = styled.div(({ theme }) => ({
	backgroundColor: theme.colors.mainwhite,
	borderRadius: 8,
	boxShadow:
		'0px 18px 65px rgba(0, 0, 0, 0.07), 0px 4.02054px 6.37957px rgba(0, 0, 0, 0.0287811), 0px 1.19702px 0px rgba(0, 0, 0, 0.0082118)',
	padding: 40,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		padding: 20,
	},
}))

const ColCard = styled.div(({ theme }) => ({}))

const ViewRowCards = styled.div(({ theme }) => ({
	display: 'grid',
	gap: 32,
	gridTemplateColumns: '1fr',
	marginBottom: 40,
	[theme.mediaQueries.mediaMinWidthTablet]: {
		gridTemplateColumns: '1fr 1fr 1fr',
	},
}))

const ImageFlamp = styled(FlampLogo)(({ theme }) => ({
	width: 93.8,
	height: 30,
}))

const ImageYandex = styled(YandexLogo)(({ theme }) => ({
	width: 82.56,
	height: 24,
}))

const ButtonFlamp = styled.a(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '13px 23px',
	border: '1px solid black',
	borderRadius: 8,
	backgroundColor: '#2F6BE5',
	'&:hover': {
		transform: 'scaleY(1.05)',
	},
	'&:active': {
		opacity: 0.75,
	},
}))

const ButtonYandex = styled.a(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	padding: '16px 30px',
	border: '1px solid black',
	borderRadius: 8,
	backgroundColor: theme.colors.mainwhite,
	'&:hover': {
		transform: 'scaleY(1.05)',
	},
	'&:active': {
		opacity: 0.75,
	},
}))

const TextOthers = styled.h4(({ theme }) => ({
	marginRight: 18,
	marginBottom: 4,
	...theme.typography.text18_24,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text20_28,
		marginBottom: 0,
	},
}))

const ViewOthersRow = styled.div(({ theme }) => ({
	display: 'flex',
	flexWrap: 'wrap',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	gap: 16,
	marginBottom: 40,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		flexWrap: 'wrap',
		flexDirection: 'row',
		alignItems: 'center',
	},
}))

const TextHeader = styled.h2(({ theme }) => ({
	marginBottom: 40,
	...theme.typography.header24_28,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header40_44,
	},
}))

const ViewRoot = styled.div(({ theme }) => ({
	marginBottom: 56,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 68,
	},
	[theme.mediaQueries.mediaMinWidthTablet]: {
		marginBottom: 94,
	},
	[theme.mediaQueries.mediaMinWidthDesktop]: {
		marginBottom: 80,
	},
}))

interface FeedbackItem {
	name?: string | undefined
	position?: string | undefined
	image?: string | undefined
	reason?: string | undefined
	text?: string | undefined
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface BlockFeedbackData extends Array<FeedbackItem> {}

interface BlockFeedbackProps {
	blockTitle?: string | undefined
	data?: BlockFeedbackData
}

export const BlockFeedback = memo(BlockFeedbackComponent)
